/**
 * One record at the centre, the records it is connected to around it.
 *
 * The same picture as the model canvas, one level down: that one draws node
 * types and the relationships the model allows, this one draws actual golden
 * records and the edges that were actually asserted between them. Sharing the
 * look is the point — a type is the same colour in both, so moving from the
 * model to a record does not mean learning a second diagram.
 *
 * One picture, not a walk. The record sits at the centre and the records it is
 * actually related to sit around it — the same thing the model canvas does for
 * node types, one level down. There is no depth control, because a record's
 * neighbourhood is a fact about that record rather than a distance someone
 * should have to dial in, and a second hop shows relationships that belong to a
 * different record's page.
 *
 * The view pans and zooms through its viewBox, exactly as the model canvas
 * does, so a big neighbourhood stays readable. Clicking a record selects it and
 * the inspector beside the canvas describes it; it does not navigate. Following
 * a record is a deliberate second click, because losing your place in a graph
 * you are exploring is the fastest way to give up on it.
 */

import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { edgeColour, nodeColour } from '../graph/palette'
import { GraphNodeCard, cardBoundary } from '../graph/GraphNodeCard'
import type { EgoEdge, EgoGraph, EgoNode } from '../types/brain.types'

const VIEW_W = 900
const VIEW_H = 560
const MIN_SPAN = VIEW_W / 3
const MAX_SPAN = VIEW_W * 2.5

/** How far the neighbours sit from the centre. Wide enough that their labels
 *  never collide, close enough that the whole thing fits without zooming. */
const RING = 190

interface View {
  x: number
  y: number
  w: number
  h: number
}

interface Placed {
  node: EgoNode
  x: number
  y: number
  r: number
}

const HOME: View = { x: -VIEW_W / 2, y: -VIEW_H / 2, w: VIEW_W, h: VIEW_H }


/**
 * The record in the middle, its neighbours around it.
 *
 * Neighbours are grouped by relationship before they are placed, so everything
 * joined by the same kind of edge sits together — which is what turns "eleven
 * circles" into "three orders, two contacts and an account". A single ring
 * rather than a force simulation: it never wobbles, and with one hop there is
 * nothing a simulation would reveal that a circle does not.
 */
function place(nodes: EgoNode[], edges: EgoEdge[], centreId: string): Placed[] {
  const centre = nodes.find((n) => n.entity_id === centreId) ?? nodes[0]
  const neighbours = nodes.filter((n) => n.entity_id !== centre.entity_id)

  // Which relationship each neighbour reached the centre by, so the ring can be
  // ordered by it. A record joined more than one way takes the first.
  const relationOf = new Map<string, string>()
  for (const edge of edges) {
    const other =
      edge.from_entity === centre.entity_id
        ? edge.to_entity
        : edge.to_entity === centre.entity_id
          ? edge.from_entity
          : null
    if (other && !relationOf.has(other)) relationOf.set(other, edge.edge_type_key)
  }

  const ordered = [...neighbours].sort((a, b) => {
    const byRelation = (relationOf.get(a.entity_id) ?? '~').localeCompare(
      relationOf.get(b.entity_id) ?? '~',
    )
    return byRelation !== 0
      ? byRelation
      : (a.label ?? '').localeCompare(b.label ?? '')
  })

  const out: Placed[] = [{ node: centre, x: 0, y: 0, r: 84 }]

  // The ring grows with the crowd rather than packing labels on top of each
  // other; past a dozen neighbours a second ring keeps them legible.
  const perRing = 12
  ordered.forEach((node, index) => {
    const ring = Math.floor(index / perRing)
    const inRing = ordered.slice(ring * perRing, (ring + 1) * perRing).length
    const step = (Math.PI * 2) / Math.max(3, inRing)
    const offset = -Math.PI / 2 + (ring % 2 === 1 ? step / 2 : 0)
    const radius = RING * (1 + ring * 0.62)
    out.push({
      node,
      x: Math.cos(offset + (index % perRing) * step) * radius * 1.25,
      y: Math.sin(offset + (index % perRing) * step) * radius,
      r: 84,
    })
  })

  return out
}

export function EgoGraphView({
  ego,
  selectedId,
  onSelect,
}: {
  ego: EgoGraph
  /** The record the inspector is describing. Null selects nothing. */
  selectedId: string | null
  onSelect: (entityId: string | null) => void
}) {
  const svgRef = useRef<SVGSVGElement>(null)
  const [view, setView] = useState<View>(HOME)
  const [panFrom, setPanFrom] = useState<{ x: number; y: number; view: View } | null>(null)
  const [hovered, setHovered] = useState<string | null>(null)
  const panMoved = useRef(false)



  const placed = useMemo(
    () => place(ego.nodes, ego.edges, ego.center.entity_id),
    [ego.nodes, ego.edges, ego.center.entity_id],
  )
  const byId = useMemo(() => {
    const map: Record<string, Placed> = {}
    for (const p of placed) map[p.node.entity_id] = p
    return map
  }, [placed])

  // Selection wins over hover, so pointing at the diagram previews a
  // neighbourhood without throwing away the record you are reading.
  const focusId = selectedId ?? hovered

  /** The focused record and everything one edge from it. */
  const neighbours = useMemo(() => {
    if (!focusId) return null
    const set = new Set<string>([focusId])
    for (const edge of ego.edges) {
      if (edge.from_entity === focusId) set.add(edge.to_entity)
      if (edge.to_entity === focusId) set.add(edge.from_entity)
    }
    return set
  }, [focusId, ego.edges])

  const aspect = useCallback(() => {
    const rect = svgRef.current?.getBoundingClientRect()
    if (!rect || !rect.width || !rect.height) return VIEW_W / VIEW_H
    return rect.width / rect.height
  }, [])

  /** Frames every record that is drawn, with room for the labels. */
  const fit = useCallback(() => {
    const ratio = aspect()
    if (!placed.length) return setView({ ...HOME, w: VIEW_H * ratio, h: VIEW_H })

    const pad = 60
    const minX = Math.min(...placed.map((p) => p.x - p.r)) - pad
    const maxX = Math.max(...placed.map((p) => p.x + p.r)) + pad
    const minY = Math.min(...placed.map((p) => p.y - p.r)) - pad
    const maxY = Math.max(...placed.map((p) => p.y + p.r)) + pad

    let w = Math.max(maxX - minX, (maxY - minY) * ratio)
    w = Math.min(MAX_SPAN, Math.max(VIEW_H * ratio * 0.75, w))
    const h = w / ratio
    setView({ x: (minX + maxX) / 2 - w / 2, y: (minY + maxY) / 2 - h / 2, w, h })
  }, [placed, aspect])

  function zoomBy(factor: number) {
    setView((v) => {
      const w = Math.min(MAX_SPAN, Math.max(MIN_SPAN, v.w / factor))
      const h = w / (v.w / v.h)
      return { x: v.x + v.w / 2 - w / 2, y: v.y + v.h / 2 - h / 2, w, h }
    })
  }

  // Refit when the shape of what is drawn changes — a new centre, or another
  // hop switched on — and never in between, so a view you panned stays put.
  const fitRef = useRef(fit)
  fitRef.current = fit
  const signature = `${ego.center.entity_id}:${ego.nodes.length}`
  const fittedFor = useRef<string | null>(null)
  useLayoutEffect(() => {
    if (!svgRef.current || !placed.length || fittedFor.current === signature) return
    fittedFor.current = signature
    fitRef.current()
  }, [signature, placed.length])

  // A record with nothing joined to it has no graph to draw. Drawing one
  // circle on an empty canvas looks like a rendering failure; saying there are
  // no relationships is the actual answer, and it is a normal one — a record
  // arrives before the things it relates to do.
  if (ego.nodes.length <= 1 || ego.edges.length === 0) {
    return (
      <div className="brain-canvas ego-empty">
        <p className="ego-emptytitle">No relationships yet</p>
        <p className="muted small">
          Nothing is linked to this record, so there is no graph to draw. Relationships appear
          here as soon as an edge is asserted between this record and another.
        </p>
      </div>
    )
  }

  return (
    <>
      <svg
        ref={svgRef}
        className="brain-canvas"
        viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="This record and the records connected to it"
        style={{ cursor: panFrom ? 'grabbing' : undefined }}
        onPointerDown={(event) => {
          if (event.target === event.currentTarget) {
            panMoved.current = false
            setPanFrom({ x: event.clientX, y: event.clientY, view })
          }
        }}
        onPointerMove={(event) => {
          if (!panFrom) return
          const svg = svgRef.current
          if (!svg) return
          const rect = svg.getBoundingClientRect()
          const dx = ((event.clientX - panFrom.x) / rect.width) * panFrom.view.w
          const dy = ((event.clientY - panFrom.y) / rect.height) * panFrom.view.h
          if (Math.abs(event.clientX - panFrom.x) > 3 || Math.abs(event.clientY - panFrom.y) > 3) {
            panMoved.current = true
          }
          setView({ ...panFrom.view, x: panFrom.view.x - dx, y: panFrom.view.y - dy })
        }}
        onPointerUp={() => {
          // A press on empty space that never became a pan puts the inspector
          // back on the record at the centre.
          if (panFrom && !panMoved.current) onSelect(null)
          setPanFrom(null)
        }}
        onPointerLeave={() => {
          setPanFrom(null)
          setHovered(null)
        }}
      >
        <defs>
          <pattern id="brain-egodots" width="52" height="52" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="0.9" className="gdot" />
          </pattern>
          <radialGradient id="brain-egovignette">
            <stop offset="0%" className="gvig-in" />
            <stop offset="100%" className="gvig-out" />
          </radialGradient>
        </defs>

        <rect
          x={view.x}
          y={view.y}
          width={view.w}
          height={view.h}
          fill="url(#brain-egovignette)"
          pointerEvents="none"
        />
        <rect
          x={view.x}
          y={view.y}
          width={view.w}
          height={view.h}
          fill="url(#brain-egodots)"
          pointerEvents="none"
        />

        <g>
          {ego.edges.map((edge) => {
            const from = byId[edge.from_entity]
            const to = byId[edge.to_entity]
            if (!from || !to) return null
            const touches =
              edge.from_entity === focusId || edge.to_entity === focusId
            const dim = Boolean(neighbours) && !touches
            const colour = edgeColour(edge.edge_type_key)
            // Pulled back to the rim of the target circle so the line stops at
            // the record instead of disappearing under it.
            const angle = Math.atan2(to.y - from.y, to.x - from.x)
            const tip = cardBoundary(from, to)
            const x2 = tip.x - Math.cos(angle) * 8
            const y2 = tip.y - Math.sin(angle) * 8
            return (
              <g key={edge.edge_id}>
                <line
                  className={`glink${dim ? ' dim' : ''}${touches ? ' lit' : ''}`}
                  stroke={colour}
                  strokeWidth={touches ? 2.4 : 1.5}
                  x1={from.x}
                  y1={from.y}
                  x2={x2}
                  y2={y2}
                />
                {/* An arrowhead, because on real records direction is the
                    difference between "placed" and "was placed by". */}
                <polygon
                  className={`ego-arrow${dim ? ' dim' : ''}`}
                  fill={colour}
                  points="0,-3.4 8,0 0,3.4"
                  transform={`translate(${x2} ${y2}) rotate(${(angle * 180) / Math.PI})`}
                />
                <text
                  className={`edgelabel${dim ? ' dim' : ''}${touches ? ' lit' : ''}`}
                  x={(from.x + x2) / 2}
                  y={(from.y + y2) / 2 - 5}
                >
                  {edge.edge_type_key}
                </text>
              </g>
            )
          })}
        </g>

        <g>
          {placed.map(({ node, x, y }) => {
            const dim = neighbours ? !neighbours.has(node.entity_id) : false
            const selected = node.entity_id === selectedId
            const isCenter = node.depth === 0
            const colour = nodeColour(node.node_type_id ?? node.node_type_key)
            return (
              <g
                key={node.entity_id}
                className={`gnode${dim ? ' dim' : ''}`}
                data-sel={selected}
                tabIndex={0}
                role="button"
                aria-label={`${node.node_type_key} ${node.label ?? ''}${
                  isCenter ? ', this record' : ''
                }`}
                onClick={() => onSelect(node.entity_id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onSelect(node.entity_id)
                  }
                }}
                onPointerEnter={() => {
                  if (!panFrom) setHovered(node.entity_id)
                }}
                onPointerLeave={() => setHovered((id) => (id === node.entity_id ? null : id))}
                style={{ cursor: 'pointer' }}
              >
                <GraphNodeCard x={x} y={y} title={node.label ?? node.entity_id.slice(0, 8)}
                  subtitle={`${node.node_type_key}${isCenter ? ' · this record' : ''}`} colour={colour} />
              </g>
            )
          })}
        </g>
      </svg>

      <div className="cp-zoom">
        <button onClick={() => zoomBy(1.25)} title="Zoom in" aria-label="Zoom in">
          +
        </button>
        <button onClick={() => zoomBy(1 / 1.25)} title="Zoom out" aria-label="Zoom out">
          −
        </button>
        <button onClick={fit} title="Fit to the neighbourhood" aria-label="Fit to the neighbourhood">
          ⤢
        </button>
      </div>
    </>
  )
}
